import { AppContext, Events } from "@Context/common/general/context";
import { AnalyticsEvents, PlaySessionTypes } from "@Enums/index";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { GetUserSessionsResponse } from "@Shared/schemas/sessions/sessionSchemas";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ACTIVITY_PAGE_SIZE, groupSessionsByMonth, type IUserSession } from "@Components/game/activity/utils";
import { Api } from "@Shared/schemas";
import { useApi } from "@hooks/index";

type ActivityPaginationStatus = "initial-loading" | "ready" | "loading-more" | "exhausted";

export interface IUseActivityReturn {
  groupedSessions: ReturnType<typeof groupSessionsByMonth>;
  isInitialLoading: boolean;
  pagination: {
    status: ActivityPaginationStatus;
    loadNextPage: () => void;
  };
  expandedMonths: Record<string, boolean>;
  expandNewMonths: (months: string[]) => void;
  toggleMonth: (month: string) => void;
  handleSessionClick: (session: IUserSession) => void;
}

export const useActivityData = (): IUseActivityReturn => {
  const { state } = useContext(AppContext);
  const location = useLocation();
  const { track } = useAnalytics();

  const [sessions, setSessions] = useState<IUserSession[]>([]);
  const [paginationStatus, setPaginationStatus] = useState<ActivityPaginationStatus>("initial-loading");
  const isLoadingMoreRef = useRef(false);
  const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>({});

  const groupedSessions = useMemo(() => groupSessionsByMonth(sessions), [sessions]);

  const { call: getUserSessions } = useApi<typeof Api.Sessions.GetUserSessions>({
    api: Api.Sessions.GetUserSessions,
    disableLoader: true,
  });

  const fetchSessions = useCallback(
    (offset: number, shouldAppend: boolean) => {
      if (!state.user.id) {
        isLoadingMoreRef.current = false;
        setSessions([]);
        setPaginationStatus("exhausted");
        return;
      }

      if (shouldAppend) {
        if (isLoadingMoreRef.current) {
          return;
        }

        isLoadingMoreRef.current = true;
      }

      setPaginationStatus(shouldAppend ? "loading-more" : "initial-loading");

      getUserSessions({
        user_id: state.user.id,
        limit: ACTIVITY_PAGE_SIZE,
        offset,
        is_completed: true,
      })
        .then((response: GetUserSessionsResponse) => {
          const page = response as IUserSession[];

          setSessions(previousSessions => (shouldAppend ? [...previousSessions, ...page] : page));
          isLoadingMoreRef.current = false;
          setPaginationStatus(page.length < ACTIVITY_PAGE_SIZE ? "exhausted" : "ready");
        })
        .catch(() => {
          if (!shouldAppend) {
            setSessions([]);
          }
          isLoadingMoreRef.current = false;
          setPaginationStatus("exhausted");
        });
    },
    [getUserSessions, state.user.id]
  );

  useEffect(() => {
    setSessions([]);
    setExpandedMonths({});
    fetchSessions(0, false);
  }, [fetchSessions]);

  const expandNewMonths = useCallback((months: string[]) => {
    setExpandedMonths(previousState => {
      const nextState = { ...previousState };
      let hasChanged = false;

      months.forEach(month => {
        if (typeof previousState[month] === "undefined") {
          nextState[month] = true;
          hasChanged = true;
        }
      });

      return hasChanged ? nextState : previousState;
    });
  }, []);

  const toggleMonth = useCallback((month: string) => {
    setExpandedMonths(previousState => ({
      ...previousState,
      [month]: !previousState[month],
    }));
  }, []);

  const loadNextPage = useCallback(() => {
    if (paginationStatus !== "ready") {
      return;
    }

    fetchSessions(sessions.length, true);
  }, [fetchSessions, paginationStatus, sessions.length]);

  const handleSessionClick = useCallback(
    (session: IUserSession) => {
      const path = `${location.pathname}/session/${session.play_session_id}`;

      track(AnalyticsEvents.results_scenario_clicked, {
        ...session,
        path,
      });

      Events.app.navigate.emit({
        path,
        delay: 150,
        data: session,
      });
    },
    [location.pathname, track]
  );

  return {
    groupedSessions,
    isInitialLoading: paginationStatus === "initial-loading",
    pagination: {
      status: paginationStatus,
      loadNextPage,
    },
    expandedMonths,
    expandNewMonths,
    toggleMonth,
    handleSessionClick,
  };
};
